// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@conklin/App/Components/Common/XngZone";
import FloatingTooltip from "@conklin/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable from "@conklin/App/Components/Header/HeaderClickable";
import MiniMyAccount from "@conklin/App/Components/MiniMyAccount/MiniMyAccount";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@conklin/App/Store/Selectors/SettingsSelectors";
import IconHeaderUser from "@conklin/XDL/Icons/IconHeaderUser";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import FieldDefinition, { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    /* <XNG-Changes> */
    accountManagementTitle = "accountManagementTitle",
    accountManagementLinks = "accountManagementLinks",
    productAndOrderManagementTitle = "productAndOrderManagementTitle",
    productAndOrderManagementLinks = "productAndOrderManagementLinks",
}

/* <XNG-Changes> */
export interface MyAccountExtraLinks {
    fields: {
        destination: LinkFieldValue;
        overrideTitle: string;
    };
}
/* <XNG-Changes> */
export interface MappedLink {
    title: string;
    url: string;
    type?: string;
}

/* <XNG-Changes> */
export const mapLinks = (state: ApplicationState, widgetLinks: LinkModel[]) => {
    const myAccountPage = getPageLinkByPageType(state, "MyAccountPage");
    return (
        widgetLinks
            ?.map(widgetLink => {
                if (widgetLink.fields.destination.type === "Page") {
                    const pageLink = getPageLinkByNodeId(state, widgetLink.fields.destination.value);
                    if (pageLink) {
                        return {
                            ...pageLink,
                            title: widgetLink.fields.overriddenTitle || pageLink?.title,
                            openInNewWindow: widgetLink.fields.openInNewWindow,
                        };
                    }
                } else if (widgetLink.fields.destination.type === "Url") {
                    const url = widgetLink.fields.destination.value;
                    const title = widgetLink.fields.overriddenTitle;

                    return { id: "", url, title };
                }
                return undefined;
            })
            // ?.filter(pageLink =>
            //     (pageLink as any)?.parentId === myAccountPage?.id ? !(pageLink as any)?.excludeFromNavigation : true,
            // ) || []
            ?.filter(pageLink => {
                // XNG-Changes: hide Change Customer when lockCustomerSelection is true
                if (
                    (pageLink as any)?.type === "ChangeCustomerPage" &&
                    getSettingsCollection(state).customSettings?.lockCustomerSelection
                ) {
                    return false;
                }
                return (pageLink as any)?.parentId === myAccountPage?.id
                    ? !(pageLink as any)?.excludeFromNavigation
                    : true;
            }) || []
    );
};

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const accountLinks = mapLinks(state, ownProps.fields.accountManagementLinks);
    const productOrdersLinks = mapLinks(state, ownProps.fields.productAndOrderManagementLinks);
    const linkGroups = [
        { title: ownProps.fields.accountManagementTitle, links: accountLinks },
        { title: ownProps.fields.productAndOrderManagementTitle, links: productOrdersLinks },
    ];

    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        // XNG-Changes
        isTabletScreen: state.xCommon.isTabletScreen,
        isAuthenticated: xIsAuthenticated(state),
        linkGroups,
        // XNG-Changes
        lockCustomerSelection: getSettingsCollection(state).customSettings?.lockCustomerSelection,
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    signInDrawer?: DrawerPresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 0;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 30px;
            text-transform: uppercase;
        `,
    },
    signInDrawer: {
        size: 333,
        position: "right",
        cssOverrides: {
            drawerContent: css`
                box-shadow: none;
                overflow: visible;
                padding-top: 24px;
                width: 100%;
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
        /* <XNG-Changes> */
        [fields.accountManagementTitle]: string;
        [fields.accountManagementLinks]: LinkModel[];
        [fields.productAndOrderManagementTitle]: string;
        [fields.productAndOrderManagementLinks]: LinkModel[];
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    // displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // XNG-Changes
    id,
    linkGroups,
    isTabletScreen,
    isAuthenticated,
    // XNG-Changes
    lockCustomerSelection,
}: Props) => {
    const [isPopupShown, setIsPopupShown] = React.useState(false);
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    // <XNG-Changes>
    const [isSignInDrawerOpen, setIsSignInDrawerOpen] = useState(false);

    useEffect(() => {
        setIsSignInDrawerOpen(false);
    }, [currentLocation]);
    // </XNG-Changes>

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "User") {
        icon = IconHeaderUser;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    // let menuItems = (myAccountPageLink?.children?.filter(
    //     pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    // ) ?? []) as MappedLink[];

    // let menuItems = myAccountPageLink?.children?.filter(pageLink => !pageLink.excludeFromNavigation);
    // XNG-Changes: hide Change Customer when lockCustomerSelection is true
    let menuItems = myAccountPageLink?.children?.filter(
        pageLink =>
            !pageLink.excludeFromNavigation &&
            !(pageLink.type === "ChangeCustomerPage" && lockCustomerSelection),
    );
    // const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
    //     return {
    //         title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
    //         iconProps: {
    //             src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
    //             size: 22,
    //             css: css`
    //                 margin-left: 20px;
    //                 margin-right: -12px;
    //             `,
    //         },
    //         clickableProps: {
    //             css: css`
    //                 display: flex;
    //                 text-transform: uppercase;
    //             `,
    //             onClick: () => {
    //                 title === "Inventory"
    //                     ? setShowInventory({ showInventory: !showInventory })
    //                     : setShowPricing({ showPricing: !showPricing });
    //             },
    //         },
    //     } ;
    // };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        // showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        // showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        e.preventDefault();

        if (isAuthenticated) {
            // XNG-CHANGES : To make sure all tippy are hidden and scrollbar is present
            history.push(myAccountPageLink?.url || "/MyAccount");
        } else if (isTabletScreen) {
            history.push(signInUrl || "/MyAccount/SignIn");
        } else {
            setIsSignInDrawerOpen(true);
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In / Register");
    if (userName && !currentUserIsGuest) {
        // XNG-Changes: change text
        signInStatusText = translate("Account");
    }

    // <XNG-Changes>
    const signInClickable = (
        <HeaderClickable
            iconProps={{ src: icon }}
            onClick={onSignInHandler}
            title={signInStatusText}
            isOpen={isPopupShown}
            showArrow={!!isAuthenticated}
        />
    );

    if (isTabletScreen) {
        return signInClickable;
    }

    if (!userName || (userName && currentUserIsGuest)) {
        return (
            <>
                {signInClickable}
                <Drawer
                    position="right"
                    {...styles.signInDrawer}
                    isOpen={isSignInDrawerOpen}
                    handleClose={() => setIsSignInDrawerOpen(false)}
                >
                    <XngZone contentId={id} zoneName="content0" />
                </Drawer>
            </>
        );
    }
    // </XNG-Changes>

    // XNG-Changes:Structure
    return (
        <FloatingTooltip
            offset={{ crossAxis: 0, mainAxis: 6 }}
            onOpenChange={open => {
                setIsPopupShown(open);
            }}
            triggerComponent={signInClickable}
            enabled={!isTabletScreen}
            extendedStyles={styles.myAccountTooltip}
            placement="bottom-end"
            showArrow={false}
        >
            <StyledWrapper
                {...{
                    css: css`
                        max-width: 430px;
                    `,
                }}
            >
                <MiniMyAccount linkGroups={linkGroups} onSignOutHandler={onSignOutHandler} />
            </StyledWrapper>
        </FloatingTooltip>
    );
};

// XNG-Changes
export const linksFieldDefinitions = [
    {
        name: fields.accountManagementTitle,
        fieldType: "Contextual",
        displayName: "Title",
        editorTemplate: "TextField",
        defaultValue: "Account Management",
    },
    {
        displayName: "Links",
        name: fields.accountManagementLinks,
        editorTemplate: "ListField",
        fieldType: "Contextual",
        getDisplay: (item: HasFields, state: any) => {
            const { type, value } = item.fields.destination;
            const overriddenTitle = item.fields.overriddenTitle;
            if (type === "Page") {
                const link = getPageLinkByNodeId(state, value);
                return overriddenTitle || link?.title;
            }
            return overriddenTitle;
        },
        defaultValue: [],
        fieldDefinitions: [
            {
                name: "destination",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                allowCategories: (item: HasFields) => false,
                allowUrls: (item: HasFields) => true,
            },
            {
                name: "overriddenTitle",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: "openInNewWindow",
                displayName: "Open in New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
    {
        name: fields.productAndOrderManagementTitle,
        fieldType: "Contextual",
        displayName: "Title",
        editorTemplate: "TextField",
        defaultValue: "Product & Order Management",
    },
    {
        displayName: "Links",
        name: fields.productAndOrderManagementLinks,
        editorTemplate: "ListField",
        fieldType: "Contextual",
        getDisplay: (item: HasFields, state: any) => {
            const { type, value } = item.fields.destination;
            const overriddenTitle = item.fields.overriddenTitle;
            if (type === "Page") {
                const link = getPageLinkByNodeId(state, value);
                return overriddenTitle || link?.title;
            }
            return overriddenTitle;
        },
        defaultValue: [],
        fieldDefinitions: [
            {
                name: "destination",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                allowCategories: (item: HasFields) => false,
                allowUrls: (item: HasFields) => true,
            },
            {
                name: "overriddenTitle",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: "openInNewWindow",
                displayName: "Open in New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            // {
            //     name: fields.includeAccountMenu,
            //     displayName: "Include My Account Menu",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: true,
            //     fieldType: "General",
            //     sortOrder: 3,
            // },
            // {
            //     name: fields.showPricingToggle,
            //     displayName: "Show/Hide Pricing Toggle",
            //     editorTemplate: "CheckboxField",
            //     isVisible: widget => widget.fields.includeAccountMenu,
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 4,
            // },
            // {
            //     name: fields.showInventoryToggle,
            //     displayName: "Show/Hide Inventory Toggle",
            //     editorTemplate: "CheckboxField",
            //     isVisible: widget => widget.fields.includeAccountMenu,
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 5,
            // },
            // XNG-Changes
            ...linksFieldDefinitions,
        ] as FieldDefinition[],
        group: "Common",
    },
};

export default widgetModule;
